import React, { useState, useRef } from "react";
import "./AddLog.scss";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCloudArrowUp,
  faHouse,
  faArrowLeft,
  faStop,
  faTrash,
  faPause,
  faPlay,
} from "@fortawesome/free-solid-svg-icons";

const AddLog = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const product = location.state?.product;

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const BATCH_SIZE = 5;
  const abortController = useRef(null);
  const fileInputRef = useRef();

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles((prev) => [...prev, ...files]);
  };

  const handleFileDrop = (event) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    setSelectedFiles((prev) => [...prev, ...files]);
  };

  const handleFileRemove = (file) => {
    setSelectedFiles((prev) => prev.filter((f) => f !== file));
  };

  const renderSelectedFiles = () => {
    return selectedFiles.map((file, index) => (
      <div key={index} className="uploadFileItem">
        <span>{file.name}</span>
        <button className="removeFiles" onClick={() => handleFileRemove(file)}>
          <FontAwesomeIcon icon={faTrash} />
        </button>
      </div>
    ));
  };

  const handleDragOver = (event) => event.preventDefault();

  const retryFetch = async (url, options, retries = 3) => {
    for (let attempt = 0; attempt < retries; attempt++) {
      try {
        const response = await fetch(url, options);
        if (response.ok) return response;
      } catch (e) {
        if (attempt === retries - 1) throw e;
        await new Promise((res) => setTimeout(res, 1000));
      }
    }
  };

  const uploadInBatches = async () => {
    setIsUploading(true);
    setIsCancelled(false);
    const totalFiles = selectedFiles.length;
    const newProgress = Array(totalFiles).fill("pending");
    setUploadProgress(newProgress);
    let uploadedFiles = [];

    for (let i = 0; i < totalFiles && !isCancelled; i += BATCH_SIZE) {
      const batchFiles = selectedFiles.slice(i, i + BATCH_SIZE);

      try {
        const requestBody = {
          fileNames: batchFiles.map((file) => file.name),
          productId: product.id,
        };

        abortController.current = new AbortController();
        const postResponse = await retryFetch(
          "https://sucmhxbjlk.execute-api.us-east-1.amazonaws.com/prod/logs/get/s3url",
          {
            method: "POST",
            body: JSON.stringify(requestBody),
            headers: { "Content-Type": "application/json" },
            signal: abortController.current.signal,
          }
        );

        const presignedUrls = await postResponse.json();

        for (let j = 0; j < batchFiles.length && !isCancelled; j++) {
          const globalIndex = i + j;
          newProgress[globalIndex] = "url";
          setUploadProgress([...newProgress]);

          // ⏸️ Pause logic
          while (isPaused && !isCancelled) {
            await new Promise((res) => setTimeout(res, 300));
          }

          await retryFetch(presignedUrls[j], {
            method: "PUT",
            headers: { "Content-Type": batchFiles[j].type },
            body: batchFiles[j],
          });

          newProgress[globalIndex] = "done";
          uploadedFiles.push(batchFiles[j]);
          setUploadProgress([...newProgress]);
        }
      } catch (err) {
        console.error("Upload error in batch", err);
        setErrorMessage("Some files failed to upload. Please try again.");
        break;
      }
    }

    if (!isCancelled) {
      setSelectedFiles((prev) => prev.filter((f) => !uploadedFiles.includes(f)));
    }

    setIsUploading(false);
  };

  const stopUpload = () => {
    setIsCancelled(true);
    if (abortController.current) {
      abortController.current.abort();
    }
    setIsUploading(false);
  };

  const togglePause = () => {
    setIsPaused((prev) => !prev);
  };

  const handleNavigateHome = () => navigate("/product");
  const handleNavigateBack = () => navigate("/logs/" + product.id, { state: { product } });

  const completed = uploadProgress.filter((p) => p === "done").length;
  const total = uploadProgress.length;
  const percentDone = total ? Math.round((completed / total) * 100) : 0;

  return (
    <div className="addLogsContainer">
      <div className="addLogsHeader">
        <div className="addLogsHeaderTitle">
          <div className="addLogsProductType">{product.product_type}</div>
          <div className="addLogsProductSerialNumber">{product.product_serial_number}</div>
        </div>
        <div className="addLogsNavigation">
          <button className="navigationButton" onClick={handleNavigateHome}>
            <FontAwesomeIcon icon={faHouse} /> My Products
          </button>
          <button className="navigationButton" onClick={handleNavigateBack}>
            <FontAwesomeIcon icon={faArrowLeft} /> Back to Logs
          </button>
        </div>
      </div>

      <div className="logsUploadContainer">
        <div className="logsDropBox" onDrop={handleFileDrop} onDragOver={handleDragOver}>
          <div className="uploadIcon">
            <br />
            <FontAwesomeIcon icon={faCloudArrowUp} />
            <br />
            <span>Drag and drop files here</span>
          </div>
          <div className="orLabel">or</div>
          <div className="customFileInput">
            <input
              id="fileInput"
              type="file"
              multiple
              ref={fileInputRef}
              onChange={handleFileSelect}
              style={{ display: "none" }}
            />
            <button
              className="customFileButton"
              onClick={() => fileInputRef.current?.click()}
            >
              Choose Files
            </button>
          </div>
        </div>

        <div className="selectedUpload">
          <div className="uploadActions">
            {!isUploading ? (
              <button className="startUpload" onClick={uploadInBatches}>
                Upload Files
              </button>
            ) : (
              <div className="uploadButtonGroup">
                <button className="cancelUpload" onClick={stopUpload}>
                  <FontAwesomeIcon icon={faStop} /> Stop
                </button>&nbsp;&nbsp;
                <button className="pauseUpload" onClick={togglePause}>
                  <FontAwesomeIcon icon={isPaused ? faPlay : faPause} /> {isPaused ? "Resume" : "Pause"}
                </button>
              </div>
            )}
            <div className="fileStatsInfo">
              <span>{selectedFiles.length} files selected</span>
              <span>{completed} files uploaded</span>
            </div>
          </div>

          {uploadProgress.length > 0 && (
            <div className="uploadProgressBarWrapper">
              <div className="uploadProgressBar">
                <div
                  className="uploadProgressFill"
                  style={{ width: `${percentDone}%` }}
                />
              </div>
              <div className="percentLabel">{percentDone}%</div>
            </div>
          )}

          {errorMessage && <div className="errorMessage">{errorMessage}</div>}

          <div className="fileListContainer">{renderSelectedFiles()}</div>
        </div>
      </div>
    </div>
  );
};

export default AddLog;
